'use client';

import { useState } from 'react';
import Link from 'next/link';
import { Search, ArrowRight, X, Shield, Bug, FileCheck, Key, Calculator, Network } from 'lucide-react';
import type { Tool, ToolCategory } from '@/lib/data/tools';

interface ToolsPageClientProps {
  tools: Tool[];
  categories: ToolCategory[];
  categoryIconMap: Record<string, React.ReactNode>;
  categoryToolCounts: Record<string, number>;
}

// Goal-based navigation data
const goalBasedNavigation = [
  {
    title: 'Secure my infrastructure',
    description: 'Scan, audit, and protect your systems',
    icon: Shield,
    color: 'from-blue-500 to-indigo-600',
    href: '/tools/security#web-security-tools',
    tools: [
      { name: 'SSL Checker', href: '/tools/security/ssl-checker' },
      { name: 'Security Headers Analyzer', href: '/tools/security/security-headers-analyzer' },
      { name: 'Subnet Calculator', href: '/tools/network/subnet-calculator' },
      { name: 'Firewall Rule Simulator', href: '/tools/network/firewall-rule-simulator' },
    ],
  },
  {
    title: 'Generate secure credentials',
    description: 'Create passwords, keys, and tokens',
    icon: Key,
    color: 'from-emerald-500 to-teal-600',
    href: '/tools/security#key-generation-tools',
    tools: [
      { name: 'Password Generator', href: '/tools/security/secure-password-generator' },
      { name: 'SSH Key Generator', href: '/tools/security/ssh-key-generator' },
      { name: 'UUID Generator', href: '/tools/developer/uuid-generator' },
      { name: 'PGP Key Generator', href: '/tools/security/pgp-key-generator' },
    ],
  },
  {
    title: 'Debug code & data',
    description: 'Validate, format, and troubleshoot',
    icon: Bug,
    color: 'from-amber-500 to-orange-600',
    href: '/tools/developer#json-data-format-tools',
    tools: [
      { name: 'JSON Validator', href: '/tools/developer/json-validator' },
      { name: 'Regex Tester', href: '/tools/developer/regex-tester' },
      { name: 'JWT Decoder', href: '/tools/security/jwt-decoder' },
      { name: 'Diff Checker', href: '/tools/developer/diff-checker' },
    ],
  },
  {
    title: 'Check compliance',
    description: 'Assess GDPR, HIPAA, and SOC 2 readiness',
    icon: FileCheck,
    color: 'from-purple-500 to-violet-600',
    href: '/tools/compliance',
    tools: [
      { name: 'GDPR Checker', href: '/tools/compliance/gdpr-checker' },
      { name: 'HIPAA Assessment', href: '/tools/compliance/hipaa-quick-assessment' },
      { name: 'SOC 2 Gap Analysis', href: '/tools/compliance/soc2-gap-analysis' },
      { name: 'Cookie Analyzer', href: '/tools/security/cookie-analyzer' },
    ],
  },
  {
    title: 'Plan IT budgets',
    description: 'Calculate costs and justify investments',
    icon: Calculator,
    color: 'from-cyan-500 to-blue-600',
    href: '/tools/planning',
    tools: [
      { name: 'Cybersecurity Budget', href: '/tools/planning/cybersecurity-budget-calculator' },
      { name: 'Data Breach Cost', href: '/tools/planning/data-breach-cost-calculator' },
      { name: 'Cloud Cost Comparison', href: '/tools/planning/cloud-cost-comparison' },
      { name: 'Cybersecurity ROI', href: '/tools/planning/cybersecurity-roi-calculator' },
    ],
  },
  {
    title: 'Investigate threats',
    description: 'Analyze IPs, domains, and malware',
    icon: Network,
    color: 'from-red-500 to-rose-600',
    href: '/tools/security#vulnerability-threat-intel',
    tools: [
      { name: 'IP Risk Checker', href: '/tools/security/ip-risk-checker' },
      { name: 'WHOIS Lookup', href: '/tools/network/whois-lookup' },
      { name: 'CVE Lookup', href: '/tools/security/cve-lookup' },
      { name: 'IOC Extractor', href: '/tools/security/ioc-extractor' },
    ],
  },
];

// Extract slug from tool link
function getToolSlug(tool: Tool): string {
  return tool.link.replace('/tools/', '');
}

export default function ToolsPageClient({
  tools,
  categories,
  categoryIconMap,
  categoryToolCounts,
}: ToolsPageClientProps) {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredTools = tools.filter((tool) => {
    if (!searchQuery.trim()) return [];
    const query = searchQuery.toLowerCase();
    return (
      tool.name.toLowerCase().includes(query) ||
      tool.description.toLowerCase().includes(query) ||
      tool.category.toLowerCase().includes(query)
    );
  });

  const isSearching = searchQuery.trim().length > 0;

  return (
    <>
      {/* Search Section */}
      <section className="py-12 px-4 bg-gray-50 border-b border-gray-200">
        <div className="max-w-2xl mx-auto">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search all tools..."
              aria-label="Search all tools"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-4 pl-12 rounded-xl border-2 border-gray-200 bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#2813e8] focus:ring-2 focus:ring-[#2813e8]/20 transition-all shadow-sm"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                aria-label="Clear search"
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
          {isSearching && (
            <p className="mt-2 text-sm text-gray-500">
              {filteredTools.length} {filteredTools.length === 1 ? 'tool' : 'tools'} found
            </p>
          )}
        </div>
      </section>

      {/* Search Results or Categories */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          {isSearching ? (
            <>
              {/* Search Results */}
              <div className="text-center mb-12">
                <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-[#2813e8] to-[#2cc3d1] bg-clip-text text-transparent">
                  Search Results
                </h2>
                <p className="text-xl text-gray-600">
                  {filteredTools.length > 0
                    ? `Found ${filteredTools.length} tools matching "${searchQuery}"`
                    : `No tools found for "${searchQuery}"`}
                </p>
              </div>

              {filteredTools.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredTools.map((tool) => (
                    <div key={tool.name} className="relative group">
                      {tool.comingSoon ? (
                        <div className="p-6 rounded-2xl border-2 border-gray-200 bg-gray-50/50 h-full relative overflow-hidden">
                          <div className="absolute top-4 right-4">
                            <span className="inline-block bg-gray-600 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-sm">
                              Coming Soon
                            </span>
                          </div>
                          <div className="flex items-start gap-3 mb-4">
                            <div className="text-4xl flex-shrink-0 opacity-50">{tool.icon}</div>
                            <h3 className="text-xl font-bold text-gray-400 leading-tight pt-1">
                              {tool.name}
                            </h3>
                          </div>
                          <p className="text-sm text-gray-400 leading-relaxed">{tool.description}</p>
                          <p className="mt-2 text-xs text-gray-400 capitalize">{tool.category}</p>
                        </div>
                      ) : (
                        <Link
                          href={`/tools/${getToolSlug(tool)}`}
                          className="p-6 rounded-2xl border-2 border-gray-200 hover:border-[#2813e8] bg-white hover:shadow-2xl transition-all duration-300 block h-full relative overflow-hidden group"
                        >
                          <div className="absolute inset-0 bg-gradient-to-br from-[#2813e8]/5 to-[#2cc3d1]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                          <div className="relative z-10">
                            <div className="flex items-start gap-3 mb-4">
                              <div className="text-4xl flex-shrink-0 transform group-hover:scale-110 transition-transform duration-300">
                                {tool.icon}
                              </div>
                              <h3 className="text-xl font-bold text-gray-900 group-hover:text-[#2813e8] transition-colors leading-tight pt-1">
                                {tool.name}
                              </h3>
                            </div>
                            <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                              {tool.description}
                            </p>
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-gray-400 capitalize bg-gray-100 px-2 py-1 rounded">
                                {tool.category}
                              </span>
                              <div className="flex items-center gap-2 text-[#2813e8] font-semibold text-sm">
                                <span>Try it now</span>
                                <ArrowRight className="w-4 h-4 group-hover:translate-x-2 transition-transform duration-300" />
                              </div>
                            </div>
                          </div>
                        </Link>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-5xl mb-4">🔍</div>
                  <h3 className="text-xl font-bold text-gray-700 mb-2">No tools found</h3>
                  <p className="text-gray-500">Try adjusting your search terms</p>
                  <button
                    onClick={() => setSearchQuery('')}
                    className="mt-4 text-[#2813e8] font-semibold hover:underline"
                  >
                    Clear search and browse categories
                  </button>
                </div>
              )}
            </>
          ) : (
            <>
              {/* Goal-Based Navigation - "I need to..." Section */}
              <div className="mb-20">
                <div className="text-center mb-12">
                  <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-[#2813e8] to-[#2cc3d1] bg-clip-text text-transparent">
                    I need to...
                  </h2>
                  <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                    Jump straight to the tools that solve your problem. Select a goal to see recommended tools.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {goalBasedNavigation.map((goal) => {
                    const IconComponent = goal.icon;
                    return (
                      <div
                        key={goal.title}
                        className="group relative p-6 rounded-2xl border-2 border-gray-200 bg-white hover:shadow-xl transition-all duration-300 overflow-hidden"
                      >
                        <div className={`absolute inset-0 bg-gradient-to-br ${goal.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300`}></div>

                        <div className="relative z-10">
                          <Link href={goal.href} className="flex items-center gap-4 mb-4 group/header">
                            <div className={`p-3 bg-gradient-to-br ${goal.color} text-white rounded-xl shadow-md group-hover/header:scale-105 transition-transform`}>
                              <IconComponent className="w-6 h-6" />
                            </div>
                            <div>
                              <h3 className="text-lg font-bold text-gray-900 group-hover/header:text-[#2813e8] transition-colors">
                                {goal.title}
                              </h3>
                              <p className="text-sm text-gray-500">{goal.description}</p>
                            </div>
                          </Link>

                          <div className="space-y-2">
                            {goal.tools.map((tool) => (
                              <Link
                                key={tool.href}
                                href={tool.href}
                                className="flex items-center justify-between p-2.5 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors group/tool"
                              >
                                <span className="text-sm font-medium text-gray-700 group-hover/tool:text-[#2813e8]">
                                  {tool.name}
                                </span>
                                <ArrowRight className="w-4 h-4 text-gray-400 group-hover/tool:text-[#2813e8] group-hover/tool:translate-x-1 transition-all" />
                              </Link>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Categories Grid */}
              <div className="text-center mb-16">
                <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-[#2813e8] to-[#2cc3d1] bg-clip-text text-transparent">
                  Browse by Category
                </h2>
                <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                  Find the right tools for your needs. Each category contains specialized utilities
                  for different IT and security tasks.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {categories.map((category) => {
                  const toolCount = categoryToolCounts[category.slug] || 0;

                  return (
                    <Link
                      key={category.slug}
                      href={`/tools/${category.slug}`}
                      className="group relative p-8 rounded-2xl border-2 border-gray-200 hover:border-[#2813e8] bg-white hover:shadow-2xl transition-all duration-300 overflow-hidden"
                    >
                      <div className="absolute inset-0 bg-gradient-to-br from-[#2813e8]/5 to-[#2cc3d1]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                      <div className="relative z-10">
                        <div className="flex items-start justify-between mb-6">
                          <div className="p-4 bg-gradient-to-br from-[#2813e8] to-[#2cc3d1] text-white rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                            {categoryIconMap[category.icon]}
                          </div>
                          <span className="text-sm font-semibold text-gray-500 bg-gray-100 px-3 py-1.5 rounded-full group-hover:bg-[#2813e8]/10 group-hover:text-[#2813e8] transition-colors">
                            {toolCount} {toolCount === 1 ? 'tool' : 'tools'}
                          </span>
                        </div>

                        <h3 className="text-2xl font-bold text-gray-900 group-hover:text-[#2813e8] transition-colors mb-3">
                          {category.name}
                        </h3>

                        <p className="text-gray-600 mb-6 leading-relaxed">{category.description}</p>

                        <div className="flex items-center gap-2 text-[#2813e8] font-semibold">
                          <span>Browse {category.name} Tools</span>
                          <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform duration-300" />
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </section>
    </>
  );
}
